// --- Client-side session state: NOTHING sensitive is ever stored here ---
//
// The JWT lives ONLY in an httpOnly, Secure, SameSite=Lax cookie set by the
// backend (auth.py: set_auth_cookie) and is never readable by JavaScript.
//
// What the SPA needs locally is just:
//   1. "Is there a session?"  -> read from the NON-httpOnly companion cookie
//      `mj_admin_session` / `mj_shop_session` ("1") that the backend sets and
//      clears together with the token. It carries no secret, works across tabs
//      (print-tag windows, deep links) and expires with the token.
//   2. A cached display profile (name / role / shop name) so the header can
//      render instantly on reload -> kept in sessionStorage (tab-scoped, gone
//      when the tab closes) and whitelisted to non-sensitive display fields.
//      When it's missing (fresh tab) AuthContext re-hydrates it from /me.
//
// localStorage is deliberately NOT used for any auth/profile/search data.

const ADMIN_MARKER = "mj_admin_session";
const SHOP_MARKER = "mj_shop_session";
const ADMIN_PROFILE = "mj_admin";
const SHOP_PROFILE = "mj_shop";

// Keys from earlier designs that lived in localStorage - purged on load so no
// browser keeps stale auth-ish data around.
const LEGACY_LOCAL_KEYS = ["mj_admin_jwt", "mj_shop_jwt", "mj_admin", "mj_shop", "mj_recent_searches"];

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}
function writeMarkerCookie(name, on) {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = on
    ? `${name}=1; Path=/; Max-Age=${60 * 60 * 24 * 30}; SameSite=Lax${secure}`
    : `${name}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
}

// One-time migration: users who logged in before the marker-cookie change have
// a valid httpOnly token but no marker. Their old localStorage flag proves a
// session existed, so mint the (non-secret) marker client-side and drop the
// legacy keys. If the token has actually expired, the first 401 clears it again.
export function migrateLegacyStorage() {
  try {
    if (localStorage.getItem(ADMIN_MARKER) && !readCookie(ADMIN_MARKER)) writeMarkerCookie(ADMIN_MARKER, true);
    if (localStorage.getItem(SHOP_MARKER) && !readCookie(SHOP_MARKER)) writeMarkerCookie(SHOP_MARKER, true);
    [ADMIN_MARKER, SHOP_MARKER, ...LEGACY_LOCAL_KEYS].forEach((k) => localStorage.removeItem(k));
  } catch { /* storage unavailable - nothing to migrate */ }
}

export const hasAdminSession = () => readCookie(ADMIN_MARKER) === "1";
export const hasShopSession = () => readCookie(SHOP_MARKER) === "1";

// Only harmless display fields are cached - never tokens, ids, phone or address.
const ADMIN_FIELDS = ["name", "email", "role", "is_manager"];
const SHOP_FIELDS = ["shop_name", "owner_name", "status", "city"];
const pick = (obj, fields) => Object.fromEntries(fields.filter((f) => obj && obj[f] !== undefined).map((f) => [f, obj[f]]));

function readProfile(key) {
  try { const v = sessionStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; }
}
function writeProfile(key, value) {
  try { value ? sessionStorage.setItem(key, JSON.stringify(value)) : sessionStorage.removeItem(key); } catch { /* noop */ }
}

export const readAdminProfile = () => readProfile(ADMIN_PROFILE);
export const readShopProfile = () => readProfile(SHOP_PROFILE);
export const cacheAdminProfile = (admin) => { const v = admin ? pick(admin, ADMIN_FIELDS) : null; writeProfile(ADMIN_PROFILE, v); return v; };
export const cacheShopProfile = (shop) => { const v = shop ? pick(shop, SHOP_FIELDS) : null; writeProfile(SHOP_PROFILE, v); return v; };

// Session start/end. The backend already set/cleared the marker cookie in the
// login/logout response; we mirror it client-side so state is correct even if
// that response is still in flight when the next render happens.
export function startAdminSession(admin) { writeMarkerCookie(ADMIN_MARKER, true); return cacheAdminProfile(admin); }
export function startShopSession(shop) { writeMarkerCookie(SHOP_MARKER, true); return cacheShopProfile(shop); }
export function clearAdminSession() { writeMarkerCookie(ADMIN_MARKER, false); writeProfile(ADMIN_PROFILE, null); }
export function clearShopSession() { writeMarkerCookie(SHOP_MARKER, false); writeProfile(SHOP_PROFILE, null); }

// Recent searches - convenience only, tab-scoped, never persisted to disk.
const RECENT_KEY = "mj_recent_searches";
export function getRecentSearches() {
  try { return JSON.parse(sessionStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
}
export function setRecentSearches(list) {
  try { sessionStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch { /* noop */ }
}
